/**
 * Bridge between React-land Clerk (hooks) and the Effect/async client runtime.
 *
 * The hosted web app is served from a different origin (app.morrowos.com) than
 * the API (api.morrowos.com), so it can't rely on a shared session cookie. The
 * primary-environment HTTP layer instead attaches the current Clerk session JWT
 * as `x-clerk-token`; the backend verifies it and mints a browser session bound
 * to the Clerk user (see apps/server auth/http.ts autoGrantClerkSession).
 *
 * Clerk lives in the React tree; the HTTP layer is plain Effect. This module is
 * the small global seam: `MorrowClerkAuthProvider` publishes a token getter and
 * a readiness signal here, and the HTTP layer / auth bootstrap read them.
 */

type ClerkTokenGetter = () => Promise<string | null>;
type OwnerIdGetter = () => string | null;

let tokenGetter: ClerkTokenGetter | null = null;
let ownerIdGetter: OwnerIdGetter | null = null;
let clerkReady = false;
const readyWaiters: Array<() => void> = [];

/** Called by the Clerk provider once Clerk has loaded and a token getter exists. */
export function publishClerkTokenGetter(getter: ClerkTokenGetter): void {
  tokenGetter = getter;
}

/** Called by the Clerk provider once Clerk has finished loading (signed in or not). */
export function markClerkReady(): void {
  if (clerkReady) return;
  clerkReady = true;
  while (readyWaiters.length > 0) {
    readyWaiters.shift()?.();
  }
}

/** Whether Clerk has finished loading (synchronous companion to {@link whenClerkReady}). */
export function isClerkReady(): boolean {
  return clerkReady;
}

/**
 * Resolve once Clerk has loaded, or after `timeoutMs` — whichever comes first.
 * The bootstrap awaits this so the very first `/auth/session` request already
 * carries the Clerk token when the user has a session, without hanging forever
 * when Clerk is slow or not configured.
 */
export function whenClerkReady(timeoutMs = 8000): Promise<void> {
  if (clerkReady) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => resolve();
    readyWaiters.push(done);
    setTimeout(done, timeoutMs);
  });
}

/** Current Clerk session JWT, or null when signed out / unavailable. */
export async function getClerkToken(): Promise<string | null> {
  // The token getter is published asynchronously once Clerk mounts. A caller that fires
  // on page mount (e.g. the Activities feed load) can race that publish and, without this
  // wait, get null → unauthenticated request → empty feed. Wait (bounded) for Clerk to be
  // ready before giving up, so the first request carries the token when a session exists.
  if (!tokenGetter) {
    await whenClerkReady();
    if (!tokenGetter) return null;
  }
  try {
    return await tokenGetter();
  } catch {
    return null;
  }
}

/**
 * Publish the chat owner id resolver: the Clerk user id (`user_…`) when signed
 * in, else a stable client-minted guest id (`guest_…`). Sent to the API so it
 * can scope/own a guest's chats without a login.
 */
export function publishOwnerIdGetter(getter: OwnerIdGetter): void {
  ownerIdGetter = getter;
}

/** Current chat owner id (`user_…` or `guest_…`), or null when unavailable. */
export function getOwnerId(): string | null {
  if (!ownerIdGetter) return null;
  try {
    return ownerIdGetter();
  } catch {
    return null;
  }
}
